/**
 * tenantResolution — pure login/switch tenant-resolution rules for the
 * multi-tenant switcher (tenant-foundation Task 11, Req 19.1–19.3).
 *
 * This module is UI-free and side-effect-free so the resolution rules can be
 * property-tested directly (the 0 / 1 / N membership cases). Given the caller's
 * Memberships plus their current `custom:activeTenantId` claim it answers two
 * questions:
 *   1. Which Tenant should the viewer be resolved INTO right now (landing)?
 *   2. Does the active-tenant claim need to be (re)established via a switch?
 *
 * The claim itself is the authoritative signal for ENFORCEMENT (see
 * `clientTenantContext`) — this helper never grants access. It only decides the
 * landing tenant and whether to nudge a server-side re-mint so a viewer whose
 * token predates their Membership (e.g. one they just joined) still drops into
 * their Tenant on login (Req 19.1).
 */

/** Tenant lifecycle status — mirrors the `TenantStatus` GraphQL enum. */
export type TenantLifecycleStatus = 'active' | 'suspended';

/**
 * One of the caller's Memberships enriched with its Tenant's display fields.
 * Mirrors the `MembershipSummary` GraphQL type (Task 11). `membershipStatus` is
 * the caller's standing ("active" | "invited"); `lastActiveAt` drives the
 * most-recently-active default (the backend returns the list already ordered
 * most-recently-active first).
 */
export interface MembershipSummary {
  tenantId: string;
  tenantName: string;
  tenantStatus: TenantLifecycleStatus;
  roles: string[];
  membershipStatus: string;
  lastActiveAt: string | null;
}

/**
 * The outcome of resolving the viewer's landing tenant:
 *   - `none`         — no landable Membership (0 memberships, or only
 *                      invited/suspended ones). Send the viewer to join/onboarding.
 *   - `resolved`     — the active-tenant claim already names a Membership the
 *                      viewer holds; land there, nothing to do.
 *   - `needs-switch` — the claim is absent or stale (names no held Membership);
 *                      switch to `tenantId` (the most-recently-active landable
 *                      default) to establish/repair the claim server-side.
 */
export type TenantResolution =
  | { kind: 'none' }
  | { kind: 'resolved'; tenantId: string }
  | { kind: 'needs-switch'; tenantId: string };

/**
 * Is this Membership one the viewer can actively LAND in / switch TO? A
 * `suspended` Tenant is shown in the switcher but is not switchable (Task 11
 * schema note); an `invited` standing is a pending Membership, not yet active.
 */
export function isSwitchableMembership(m: MembershipSummary): boolean {
  return m.tenantStatus === 'active' && m.membershipStatus === 'active';
}

/** Normalize the raw claim to a trimmed, non-empty string or `undefined`. */
function normalizeClaim(activeTenantId: string | null | undefined): string | undefined {
  return typeof activeTenantId === 'string' && activeTenantId.trim().length > 0
    ? activeTenantId.trim()
    : undefined;
}

/**
 * Resolve the viewer's landing tenant from their active-tenant claim + the
 * (most-recently-active-first) list of their Memberships. Pure — see the module
 * header. Property-tested for the 0 / 1 / N cases.
 */
export function resolveActiveTenant(
  activeTenantId: string | null | undefined,
  memberships: readonly MembershipSummary[],
): TenantResolution {
  if (memberships.length === 0) return { kind: 'none' };

  const claim = normalizeClaim(activeTenantId);

  // If the claim names a Membership the viewer actually holds, honor it — the
  // server minted it after validating that Membership, so it is the current
  // tenant regardless of the tenant's present lifecycle status (a tenant
  // suspended mid-session keeps the claim; the switcher reflects the state).
  if (claim && memberships.some((m) => m.tenantId === claim)) {
    return { kind: 'resolved', tenantId: claim };
  }

  // Claim absent or stale → pick the most-recently-active LANDABLE default.
  // The list is pre-sorted most-recently-active first and `filter` is stable,
  // so the first landable entry is the correct default (Req 19.2).
  const landable = memberships.filter(isSwitchableMembership);
  if (landable.length === 0) return { kind: 'none' };
  return { kind: 'needs-switch', tenantId: landable[0].tenantId };
}
